"use client";

import { useCallback, useState, useTransition } from "react";
import { flushSync } from "react-dom";

/**
 * Hook for button actions that perform async work then navigate.
 *
 * Solves the two-part problem:
 * 1. Show spinner IMMEDIATELY (flushSync forces render before async work)
 * 2. Run async work with React's concurrent features (useTransition)
 *
 * Shows spinner when action starts (isStarted) or during React transition (isTransitioning)
 * Without flushSync, navigation can happen before React renders the spinner.
 * React recommends marking navigations as Transitions for non-blocking updates:
 * @see https://react.dev/reference/react/useTransition
 *
 * @example
 * const { isPending, startAction } = useButtonPendingAction();
 *
 * const handleClick = () => {
 *   startAction(async () => {
 *     await serverAction();
 *     router.push('/next');
 *   });
 * };
 *
 * <Button loading={isPending} disabled={isPending} onClick={handleClick} />
 */
export function useButtonPendingAction() {
  const [isStarted, setIsStarted] = useState(false);
  const [isTransitioning, startTransition] = useTransition();

  const startAction = useCallback(
    (action: () => void | Promise<void>) => {
      // flushSync forces immediate re-render so spinner shows before async work
      flushSync(() => setIsStarted(true));

      // useTransition for React concurrent rendering benefits
      startTransition(async () => {
        try {
          await action();
        } finally {
          setIsStarted(false);
        }
      });
    },
    [startTransition]
  );

  return { isPending: isStarted || isTransitioning, startAction };
}
